import React, { useCallback, useImperativeHandle, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import SydModal from '../../../../commonDesign/SydModal'
import SydButton from '../../../../commonDesign/Button'
import SydInput from '../../../../commonDesign/SydInput'
import { useListUserDqlQueries, useFavoriteUserDqlQuery, useRemoveUserDqlQuery } from '../../../../../api/denali'
import FavoriteStarButton from './FavoriteStarButton'
import { formatQueryDate } from './utils'

const useStyles = makeStyles(() => ({
  container: {
    padding: '0 16px 16px',
    minHeight: '300px',
    maxHeight: '500px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  list: {
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '32px 1fr auto auto',
    alignItems: 'center',
    gap: '8px',
    padding: '8px',
    borderRadius: '4px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'var(--surface-bg-color-d4)'
    }
  },
  rowSelected: {
    backgroundColor: 'color-mix(in srgb, var(--color-primary-500) 15%, transparent)',
    outline: '1px solid color-mix(in srgb, var(--color-primary-500) 40%, transparent)',
    outlineOffset: '-1px'
  },
  queryInfo: {
    minWidth: 0
  },
  queryName: {
    fontSize: '14px',
    fontWeight: 500,
    color: 'var(--font-color-main)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  queryDescription: {
    fontSize: '12px',
    color: 'var(--color-contrast-neutral-300)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  queryDate: {
    fontSize: '12px',
    color: 'var(--color-contrast-neutral-300)',
    whiteSpace: 'nowrap'
  },
  deleteBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '4px',
    color: 'var(--color-contrast-neutral-300)',
    '&:hover': {
      color: 'var(--color-error-500)'
    }
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    padding: '0 16px 16px'
  },
  empty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    color: 'var(--color-contrast-neutral-300)',
    fontSize: '14px'
  },
  processing: {
    opacity: 0.5,
    pointerEvents: 'none'
  },
  rowDeleting: {
    opacity: 0.4,
    textDecoration: 'line-through'
  }
}))

const OpenQueryDialog = React.forwardRef(function OpenQueryDialog ({ onOpen }, ref) {
  const classes = useStyles()
  const [dialogState, setDialogState] = useState({ open: false })
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedId, setSelectedId] = useState(null)
  const [favoritingId, setFavoritingId] = useState(null)
  const [deletingId, setDeletingId] = useState(null)

  const { data, isLoading } = useListUserDqlQueries({ enabled: dialogState.open })
  const { mutate: toggleFavorite } = useFavoriteUserDqlQuery()
  const { mutate: removeQuery } = useRemoveUserDqlQuery()

  const queries = useMemo(() => {
    const list = data?.queries || []
    if (!searchTerm.trim()) return list
    const term = searchTerm.toLowerCase()
    return list.filter(q =>
      q.name.toLowerCase().includes(term) ||
      (q.description && q.description.toLowerCase().includes(term))
    )
  }, [data, searchTerm])

  const close = useCallback(() => {
    setDialogState({ open: false })
    setSearchTerm('')
    setSelectedId(null)
  }, [])

  useImperativeHandle(ref, () => ({
    open: () => {
      setDialogState({ open: true })
      setSearchTerm('')
      setSelectedId(null)
    }
  }), [])

  const handleOpen = useCallback(() => {
    const query = queries.find(q => q.userDqlQueryId === selectedId)
    if (query) {
      onOpen(query)
      close()
    }
  }, [selectedId, queries, onOpen, close])

  const handleDoubleClick = useCallback((query) => {
    onOpen(query)
    close()
  }, [onOpen, close])

  const handleFavorite = useCallback((e, query) => {
    e.stopPropagation()
    setFavoritingId(query.userDqlQueryId)
    toggleFavorite(
      { userDqlQueryId: query.userDqlQueryId, isFavorite: !query.isFavorite },
      { onSettled: () => setFavoritingId(null) }
    )
  }, [toggleFavorite])

  const handleDelete = useCallback((e, query) => {
    e.stopPropagation()
    if (window.confirm(`Delete "${query.name}"? This cannot be undone.`)) {
      setDeletingId(query.userDqlQueryId)
      removeQuery(
        { userDqlQueryId: query.userDqlQueryId },
        {
          onSettled: () => setDeletingId(null),
          onSuccess: () => {
            if (selectedId === query.userDqlQueryId) {
              setSelectedId(null)
            }
          }
        }
      )
    }
  }, [removeQuery, selectedId])

  return (
    <SydModal
      title='Open Saved Query'
      open={dialogState.open}
      onClose={close}
      size='medium'
      position='top'
      disablePortal={false}
    >
      <div className={classes.container}>
        <SydInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder='Search saved queries...'
          iconName='search'
          size='sm'
          minWidth='0'
          autoFocus
        />
        <div className={classes.list}>
          {isLoading ? (
            <div className={classes.empty}>Loading...</div>
          ) : queries.length === 0 ? (
            <div className={classes.empty}>
              {searchTerm ? 'No queries match your search' : 'No saved queries yet'}
            </div>
          ) : (
            queries.map(query => (
              <div
                key={query.userDqlQueryId}
                className={`${classes.row} ${selectedId === query.userDqlQueryId ? classes.rowSelected : ''}`}
                onClick={() => setSelectedId(query.userDqlQueryId)}
                onDoubleClick={() => handleDoubleClick(query)}
              >
                <FavoriteStarButton
                  isFavorite={query.isFavorite}
                  loading={favoritingId === query.userDqlQueryId}
                  onClick={(e) => handleFavorite(e, query)}
                  size='sm'
                />
                <div className={`${classes.queryInfo} ${deletingId === query.userDqlQueryId ? classes.rowDeleting : ''}`}>
                  <div className={classes.queryName}>{query.name}</div>
                  {query.description && (
                    <div className={classes.queryDescription}>{query.description}</div>
                  )}
                </div>
                <div className={`${classes.queryDate} ${deletingId === query.userDqlQueryId ? classes.rowDeleting : ''}`}>
                  {formatQueryDate(query.modifiedDate)}
                </div>
                <button
                  className={`${classes.deleteBtn} ${deletingId === query.userDqlQueryId ? classes.processing : ''}`}
                  onClick={(e) => handleDelete(e, query)}
                  disabled={deletingId === query.userDqlQueryId}
                  title='Delete query'
                >
                  {deletingId === query.userDqlQueryId ? '\u2026' : '\u2715'}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
      <div className={classes.actions}>
        <SydButton size='sm' variant='ghost' onClick={close}>
          Cancel
        </SydButton>
        <SydButton size='sm' variant='primary' onClick={handleOpen} disabled={!selectedId}>
          Open
        </SydButton>
      </div>
    </SydModal>
  )
})

OpenQueryDialog.propTypes = {
  onOpen: PropTypes.func
}

OpenQueryDialog.defaultProps = {
  onOpen: () => {}
}

export default OpenQueryDialog
